import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Initials } from "@/components/job/Lifecycle";
import { ago, cn } from "@/lib/utils";
import { Link2, Loader2, Send } from "lucide-react";

const statusOf = (m) => m.decision === "approved" ? ["Approved", "text-primary"]
  : m.decision === "needs_changes" ? ["Asked for changes", "text-amber-600 dark:text-amber-300"]
  : m.requested_at ? [`Asked ${ago(m.requested_at)}`, "text-muted-foreground"] : null;

const sentMessage = (reqs) => {
  const names = reqs.map((r) => r.name || r.email).join(", ");
  const s = new Set(reqs.map((r) => r.email_status));
  if ([...s].every((x) => x === "sent")) return [`Sent to ${names}`, "They'll get an email with a link to the report."];
  if (s.has("recent")) return [`Asked ${names} again`, "They were emailed a few minutes ago, so no new email was sent."];
  if (s.has("not_configured")) return [`Asked ${names}`, "Email isn't set up for your team, so they'll see it on their Home page. You can also copy the link."];
  return [`Asked ${names}`, "They'll see the request on their Home page."];
};

const copyReport = async (trialId) => {
  const url = `${window.location.origin}/recruiter/trial-report/${trialId}`;
  try { await navigator.clipboard.writeText(url); toast.success("Report link copied"); } catch { toast.message("Report link", { description: url }); }
};

function Row({ m, on, toggle }) {
  const st = statusOf(m);
  const done = m.decision === "approved";
  return (
    <li>
      <label className={cn("flex items-center gap-3 px-3.5 py-2.5", done ? "opacity-70" : "cursor-pointer hover:bg-foreground/[0.03] transition-colors")} data-testid={`ask-teammate-${m.id}`}>
        <Checkbox checked={on} onCheckedChange={() => toggle(m.id)} disabled={done} aria-label={`Ask ${m.name || m.email}`} data-testid={`ask-check-${m.id}`} />
        <Initials name={m.name || m.email} />
        <span className="min-w-0 flex-1 leading-tight"><span className="block text-sm font-medium truncate">{m.name || m.email}</span><span className="block text-xs text-muted-foreground truncate">{m.email}</span></span>
        {st && <span className={cn("text-xs shrink-0", st[1])} data-testid={`ask-status-${m.id}`}>{st[0]}</span>}
      </label>
    </li>
  );
}

// Pick teammates and send them the trial report to approve (email + their Home page).
export function AskTeammates({ trialId, onSent }) {
  const [list, setList] = useState(null);
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const load = useCallback(() => api.get(`/test/trial/${trialId}/reviewers`).then((r) => setList(r.data)).catch(() => setList([])), [trialId]);
  useEffect(() => { load(); }, [load]);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/test/trial/${trialId}/request-signoff`, { reviewer_ids: picked, note });
      const [title, description] = sentMessage(data.requests);
      toast.success(title, { description });
      setPicked([]); setNote(""); load(); onSent?.(data.requests);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the request"); }
    finally { setSending(false); }
  };

  if (!list) return <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="ask-loading"><Loader2 className="h-4 w-4 motion-safe:animate-spin text-primary" />Loading your team…</p>;
  if (!list.length) return <p className="text-sm text-muted-foreground" data-testid="ask-empty">Nobody else is on your team yet. <Link to="/account" className="text-primary hover:underline" data-testid="ask-invite-link">Invite a teammate</Link>, or approve it yourself.</p>;
  return (
    <div className="space-y-3" data-testid="ask-teammates">
      <ul className="rounded-xl border border-border divide-y divide-border max-h-64 overflow-y-auto">
        {list.map((m) => <Row key={m.id} m={m} on={picked.includes(m.id)} toggle={toggle} />)}
      </ul>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Add a note for them (optional)" data-testid="ask-note" />
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={send} disabled={!picked.length || sending} className="gap-1.5" data-testid="ask-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{picked.length > 1 ? `Send to ${picked.length} teammates` : "Send for review"}
        </Button>
        <Button variant="ghost" onClick={() => copyReport(trialId)} className="gap-1.5 text-muted-foreground" data-testid="ask-copy-link"><Link2 className="h-4 w-4" />Copy link instead</Button>
      </div>
    </div>
  );
}

export function AskTeammateDialog({ trialId, open, onOpenChange, onSent }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="ask-dialog">
        <DialogHeader>
          <DialogTitle>Ask a teammate to review</DialogTitle>
          <DialogDescription>Get a second look before publishing. We'll email them a link to the report and add it to their Home page. You can still approve it yourself at any time.</DialogDescription>
        </DialogHeader>
        {open && trialId && <AskTeammates trialId={trialId} onSent={(r) => { onSent?.(r); onOpenChange(false); }} />}
      </DialogContent>
    </Dialog>
  );
}
